import { useEffect, useState } from "react";
import { httpService } from "../../base/services/httpService.service";
import { LocalStorageService } from "../../base/services/local-storage.service";
import { UserNotificationsEnum } from "../auth/userNotifications.enum";

// The endpoint requires a workspaceId but ignores its value, and these notifications
// come without one.
const ANY_WORKSPACE = "00000000-0000-0000-0000-000000000000";

// assessmentId -> Promise<name | null>, shared by the page and the bell.
const fillerNames = new Map();

/**
 * "Assessment is completed" notifications are stored by the backend as
 * `... is completed by ` with the name missing. Until the backend fills it, we look up
 * who filled the assessment (entityId).
 */
const needsName = (notification) =>
   notification?.notificationType === UserNotificationsEnum.NgoCompletedAssessment &&
   !!notification.entityId &&
   /\s$/.test(notification.notificationBody || "");

/**
 * Staff assigned to the modules fill them; with none, the assessment's expert does.
 * The API has no approver or "submitted by", so those can't be named.
 */
const getFillerName = (assessment) => {
   const staff = [
      ...new Set((assessment?.surveys || []).map((s) => s?.staff?.name).filter(Boolean))
   ];
   if (staff.length) return staff.join(", ");
   return assessment?.expert?.name || null;
};

const fetchFillerName = (assessmentId) => {
   if (!fillerNames.has(assessmentId)) {
      const organizationId = LocalStorageService.getItem("user")?.organizationId;
      fillerNames.set(
         assessmentId,
         new Promise((resolve) => {
            // A 401 (the token was refreshing) isn't cached, so the next list update asks
            // again. Anything else, e.g. a 404 for a deleted assessment, stays "no name".
            const fail = (error) => {
               if (error?.response?.status === 401) fillerNames.delete(assessmentId);
               resolve(null);
            };
            httpService
               .get(
                  `Assesments/${assessmentId}/surveys?organizationId=${organizationId}&workspaceId=${ANY_WORKSPACE}`,
                  (res) => resolve(getFillerName(res?.data)),
                  fail
               )
               .catch(fail);
         })
      );
   }
   return fillerNames.get(assessmentId);
};

/** Who filled each assessment, keyed by assessment id, for notifications missing a name. */
export function useCompletedByNames(notifications) {
   const [names, setNames] = useState({});

   useEffect(() => {
      let active = true;
      const ids = [...new Set((notifications || []).filter(needsName).map((n) => n.entityId))];
      ids.forEach((id) =>
         fetchFillerName(id).then((name) => {
            if (active && name) setNames((prev) => (prev[id] ? prev : { ...prev, [id]: name }));
         })
      );
      return () => {
         active = false;
      };
   }, [notifications]);

   return names;
}

/**
 * The notification's body. Where the backend left the name out, it gets the name we
 * found, or, when there is none, ends at "... is completed." instead of a dangling "by".
 */
export function getNotificationBody(notification, namesById, t) {
   const body = notification?.notificationBody || "";
   if (!needsName(notification)) return body;

   const name = namesById?.[notification.entityId];
   if (name) return `${body.trimEnd()} ${name}`;

   const start = body.indexOf('"');
   const end = body.lastIndexOf('"');
   if (start === -1 || end <= start) return body;
   return `${t("guided-assessment-completed-prefix-text")} ${body.slice(start, end + 1)} ${t(
      "guided-assessment-completed-suffix-text"
   )}`;
}
